import { HttpMethods, makeRequest, type MakeRequest } from "../helpers/http.helper";
import { CreatePreSignedUrlRequest, CreatePreSignedUrlResponse, FileType, FileExtension, getFileExtension } from "../types/upload.type";

function unwrapPresignedResponse(raw: unknown): CreatePreSignedUrlResponse {
  if (
    raw &&
    typeof raw === "object" &&
    "result" in raw &&
    (raw as { result: unknown }).result &&
    typeof (raw as { result: unknown }).result === "object"
  ) {
    return (raw as { result: CreatePreSignedUrlResponse }).result;
  }
  return raw as CreatePreSignedUrlResponse;
}

export const generatePresignedUrl = (
  data: CreatePreSignedUrlRequest,
  fileType: FileType,
  fileExtension: FileExtension,
  extraHeaders?: Record<string, string>,
): Promise<CreatePreSignedUrlResponse> => {
  return makeRequest({
    url: "/upload",
    method: HttpMethods.POST,
    data,
    params: {
      file_type: fileType,
      file_extension: fileExtension,
    },
    headers: extraHeaders as MakeRequest["headers"],
  }).then(unwrapPresignedResponse);
};

export const uploadFileToPresignedUrl = async (file: File, presignedUrl: string, contentType?: string): Promise<void> => {
  const response = await fetch(presignedUrl, {
    method: HttpMethods.PUT,
    body: file,
    headers: contentType ? { "Content-Type": contentType } : undefined,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
  }
};

/** Presign + PUT to storage; returns `asset_id` (presigned `id`) and filename to attach elsewhere. */
export async function uploadFileAsNewAsset(file: File, fileType: FileType = FileType.CUSTOM): Promise<{ asset_id: string; filename: string }> {
  const fileExtension = getFileExtension(file.name);
  if (!fileExtension) {
    throw new Error(`Unsupported file type. Supported types: ${Object.values(FileExtension).join(", ")}`);
  }
  const presigned = await generatePresignedUrl({ original_name: file.name }, fileType, fileExtension);
  await uploadFileToPresignedUrl(file, presigned.upload_url);
  return { asset_id: presigned.id, filename: file.name };
}
